(() => {
  const ROOT_SELECTOR = '[data-ag-homepage-videos]';
  const API_SRC = 'https://www.youtube.com/iframe_api';
  const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], iframe, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const decodeEntities = (value) => {
    const decoder = document.createElement('textarea');
    let decoded = String(value || '');

    for (let pass = 0; pass < 2; pass += 1) {
      decoder.innerHTML = decoded;
      const next = decoder.value;
      if (next === decoded) break;
      decoded = next;
    }

    return decoded;
  };

  const normalizeExtract = (value) => decodeEntities(value)
    .trim()
    .replace(/^(\*\*|__)([\s\S]+)\1$/, '$2')
    .replace(/^(\*|_)([\s\S]+)\1$/, '$2')
    .trim();

  const loadYouTubeApi = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (window.__agYouTubeApiPromise) return window.__agYouTubeApiPromise;

    window.__agYouTubeApiPromise = new Promise((resolve, reject) => {
      const previousReady = window.onYouTubeIframeAPIReady;
      const timeoutId = window.setTimeout(() => reject(new Error('YouTube player API timed out.')), 12000);

      window.onYouTubeIframeAPIReady = () => {
        if (typeof previousReady === 'function') previousReady();
        window.clearTimeout(timeoutId);
        resolve(window.YT);
      };

      let script = document.querySelector(`script[src="${API_SRC}"]`);
      if (!script) {
        script = document.createElement('script');
        script.src = API_SRC;
        script.async = true;
        script.addEventListener('error', () => {
          window.clearTimeout(timeoutId);
          reject(new Error('YouTube player API failed to load.'));
        });
        document.head.appendChild(script);
      }
    });

    return window.__agYouTubeApiPromise;
  };

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.agHomepageVideosInit === 'true') return;

    const dialog = root.querySelector('[data-ag-video-dialog]');
    const playerContainer = root.querySelector('[data-ag-video-player]');
    const loading = root.querySelector('[data-ag-video-loading]');
    const error = root.querySelector('[data-ag-video-error]');
    const title = root.querySelector('[data-ag-video-dialog-title]');
    const creator = root.querySelector('[data-ag-video-dialog-creator]');
    const closeButton = root.querySelector('[data-ag-video-close]');
    const triggers = Array.from(root.querySelectorAll('[data-ag-video-trigger]'));
    const extracts = Array.from(root.querySelectorAll('.ag-homepage-videos__description'));
    const carousel = root.querySelector('[data-ag-scroll-carousel]');
    const track = root.querySelector('[data-ag-scroll-track]');

    if (!(dialog instanceof HTMLDialogElement) || !(playerContainer instanceof HTMLElement)) return;
    if (!(loading instanceof HTMLElement) || !(error instanceof HTMLElement)) return;
    if (!(title instanceof HTMLElement) || !(creator instanceof HTMLElement)) return;
    if (!(closeButton instanceof HTMLButtonElement) || !triggers.length) return;

    root.dataset.agHomepageVideosInit = 'true';

    let player = null;
    let activeTrigger = null;
    let playerSerial = 0;
    let autoplayTimer = null;
    let carouselVisible = true;
    let extractSyncFrame = 0;

    const syncExtractHeights = () => {
      if (extractSyncFrame) window.cancelAnimationFrame(extractSyncFrame);
      extracts.forEach((extract) => {
        if (extract instanceof HTMLElement) extract.style.minHeight = '';
      });
      if (extracts.length < 2) return;

      extractSyncFrame = window.requestAnimationFrame(() => {
        extractSyncFrame = 0;
        const tallest = Math.max(...extracts.map((extract) => (
          extract instanceof HTMLElement ? extract.getBoundingClientRect().height : 0
        )));
        if (tallest <= 0) return;
        extracts.forEach((extract) => {
          if (extract instanceof HTMLElement) extract.style.minHeight = `${Math.ceil(tallest)}px`;
        });
      });
    };

    const stopAutoplay = () => {
      if (autoplayTimer) window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const rotateCarousel = () => {
      if (!(track instanceof HTMLElement) || document.hidden || !carouselVisible || dialog.open) return;
      const cards = Array.from(track.children).filter((node) => node instanceof HTMLElement);
      const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      if (cards.length < 2 || maxScrollLeft <= 2) return;
      const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : track.clientWidth;
      const nextLeft = track.scrollLeft >= maxScrollLeft - 2
        ? 0
        : Math.min(maxScrollLeft, track.scrollLeft + Math.max(1, step));
      track.scrollTo({ left: nextLeft, behavior: 'smooth' });
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const delay = Math.max(3, Number(root.dataset.autoplaySeconds || 6)) * 1000;
      autoplayTimer = window.setInterval(rotateCarousel, delay);
    };

    const destroyPlayer = () => {
      playerSerial += 1;
      if (player && typeof player.destroy === 'function') {
        player.destroy();
      }
      player = null;
      playerContainer.replaceChildren();
    };

    const cleanupDialog = () => {
      destroyPlayer();
      document.body.classList.remove('overflow-hidden');
      loading.hidden = false;
      error.hidden = true;

      if (activeTrigger instanceof HTMLElement && document.contains(activeTrigger)) {
        activeTrigger.focus();
      }
      activeTrigger = null;
    };

    const closeDialog = () => {
      if (dialog.open) dialog.close();
    };

    const showError = () => {
      loading.hidden = true;
      error.hidden = false;
      playerContainer.replaceChildren();
    };

    const createPlayer = async (trigger) => {
      const videoId = trigger.dataset.videoId || '';
      const videoTitle = trigger.dataset.videoTitle || 'Video';
      const startSeconds = Math.max(0, Number.parseInt(trigger.dataset.videoStart || '0', 10) || 0);
      const showCaptions = trigger.dataset.videoCaptions === 'true';
      const locale = document.documentElement.lang || 'en';
      const serial = ++playerSerial;

      loading.hidden = false;
      error.hidden = true;
      playerContainer.replaceChildren();

      try {
        const YT = await loadYouTubeApi();
        if (!dialog.open || serial !== playerSerial) return;

        const frame = document.createElement('iframe');
        const params = new URLSearchParams({
          autoplay: '1',
          controls: '1',
          enablejsapi: '1',
          playsinline: '1',
          rel: '0',
          hl: locale,
          origin: window.location.origin,
        });

        if (startSeconds > 0) params.set('start', String(startSeconds));
        if (showCaptions) {
          params.set('cc_load_policy', '1');
          params.set('cc_lang_pref', locale.split('-')[0]);
        }

        frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
        frame.title = videoTitle;
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        frame.allowFullscreen = true;
        frame.referrerPolicy = 'strict-origin-when-cross-origin';
        playerContainer.appendChild(frame);

        player = new YT.Player(frame, {
          events: {
            onReady: () => {
              if (serial !== playerSerial) return;
              loading.hidden = true;
            },
            onError: () => {
              if (serial !== playerSerial) return;
              destroyPlayer();
              showError();
            },
          },
        });
      } catch (_error) {
        if (serial !== playerSerial) return;
        destroyPlayer();
        showError();
      }
    };

    const openVideo = (trigger) => {
      stopAutoplay();
      activeTrigger = trigger;
      title.textContent = normalizeExtract(trigger.dataset.videoExtract)
        || decodeEntities(trigger.dataset.videoTitle)
        || 'Video';

      const creatorName = trigger.dataset.videoCreator || '';
      creator.textContent = decodeEntities(creatorName);
      creator.hidden = !creatorName;

      document.body.classList.add('overflow-hidden');
      dialog.showModal();
      closeButton.focus();
      createPlayer(trigger);
    };

    const onDialogClick = (event) => {
      if (event.target === dialog) closeDialog();
    };

    const onCarouselMouseEnter = () => stopAutoplay();
    const onCarouselMouseLeave = () => startAutoplay();
    const onCarouselFocusIn = () => stopAutoplay();
    const onCarouselFocusOut = () => {
      window.setTimeout(() => {
        if (!(carousel instanceof HTMLElement) || !carousel.contains(document.activeElement)) startAutoplay();
      }, 0);
    };
    const onVisibilityChange = () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    };

    const onDialogKeydown = (event) => {
      if (event.key !== 'Tab') return;

      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => element instanceof HTMLElement && !element.hidden,
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const triggerHandlers = new Map();
    triggers.forEach((trigger) => {
      if (!(trigger instanceof HTMLButtonElement)) return;
      const handler = () => openVideo(trigger);
      trigger.addEventListener('click', handler);
      triggerHandlers.set(trigger, handler);
    });

    closeButton.addEventListener('click', closeDialog);
    dialog.addEventListener('click', onDialogClick);
    dialog.addEventListener('keydown', onDialogKeydown);
    dialog.addEventListener('close', cleanupDialog);
    carousel?.addEventListener('mouseenter', onCarouselMouseEnter);
    carousel?.addEventListener('mouseleave', onCarouselMouseLeave);
    carousel?.addEventListener('focusin', onCarouselFocusIn);
    carousel?.addEventListener('focusout', onCarouselFocusOut);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('resize', syncExtractHeights, { passive: true });

    let carouselObserver = null;
    if (carousel instanceof HTMLElement && typeof IntersectionObserver !== 'undefined') {
      carouselObserver = new IntersectionObserver(([entry]) => {
        carouselVisible = Boolean(entry?.isIntersecting);
        if (carouselVisible) startAutoplay();
        else stopAutoplay();
      }, { threshold: 0.2 });
      carouselObserver.observe(carousel);
    }
    syncExtractHeights();
    if (document.fonts?.ready) {
      document.fonts.ready.then(syncExtractHeights).catch(() => {});
    }
    startAutoplay();

    root.__agHomepageVideosCleanup = () => {
      triggerHandlers.forEach((handler, trigger) => trigger.removeEventListener('click', handler));
      closeButton.removeEventListener('click', closeDialog);
      dialog.removeEventListener('click', onDialogClick);
      dialog.removeEventListener('keydown', onDialogKeydown);
      dialog.removeEventListener('close', cleanupDialog);
      carousel?.removeEventListener('mouseenter', onCarouselMouseEnter);
      carousel?.removeEventListener('mouseleave', onCarouselMouseLeave);
      carousel?.removeEventListener('focusin', onCarouselFocusIn);
      carousel?.removeEventListener('focusout', onCarouselFocusOut);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('resize', syncExtractHeights);
      if (extractSyncFrame) window.cancelAnimationFrame(extractSyncFrame);
      extracts.forEach((extract) => {
        if (extract instanceof HTMLElement) extract.style.minHeight = '';
      });
      carouselObserver?.disconnect();
      stopAutoplay();
      if (dialog.open) dialog.close();
      destroyPlayer();
      document.body.classList.remove('overflow-hidden');
      root.dataset.agHomepageVideosInit = 'false';
    };
  };

  const initAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const roots = event.target.matches(ROOT_SELECTOR)
      ? [event.target]
      : Array.from(event.target.querySelectorAll(ROOT_SELECTOR));
    roots.forEach((root) => root.__agHomepageVideosCleanup?.());
  });
})();
